import { EmptyState } from '@agentic-flow-studio/shared/components/empty-state';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Card } from '@agentic-flow-studio/shared/ui/card';
import { Building, Edit2, MapPin, Plus, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function LocationsPage() {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const navigate = useNavigate();
  const basePath = `/org/${orgId}/workspace/${workspaceId}/locations`;
  const utils = trpc.useUtils();

  const { data: locations = [], isLoading } = trpc.jobPlan.locations.list.useQuery();

  const deleteLocationMutation = trpc.jobPlan.locations.delete.useMutation({
    onSuccess: () => {
      toast.success('Location deleted');
      utils.jobPlan.locations.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Action Bar */}
      <div className="flex items-center justify-between p-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight">Depots & Locations</h1>
          <Badge variant="outline" className="text-xs">
            {locations.length} Saved
          </Badge>
        </div>

        <Button size="sm" asChild className="gap-1.5">
          <Link to={`${basePath}/new`}>
            <Plus className="h-4 w-4" />
            <span>Add Location</span>
          </Link>
        </Button>
      </div>

      {/* Main Locations List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            Loading locations...
          </div>
        ) : locations.length === 0 ? (
          <EmptyState
            icon={Building}
            title="No locations saved"
            description="Define start depots, warehouses, and meeting points for operators."
            action={
              <Button size="sm" asChild>
                <Link to={`${basePath}/new`}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  <span>Add Location</span>
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {locations.map((loc) => (
              <Card
                key={loc.id}
                className="p-4 flex flex-col justify-between gap-3 transition-shadow hover:shadow-md cursor-pointer group"
                onClick={() => navigate(`${basePath}/${loc.id}/edit`)}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded bg-primary/10 text-primary flex items-center justify-center">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <span className="font-semibold text-sm">{loc.label}</span>
                    </div>

                    <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${basePath}/${loc.id}/edit`);
                        }}
                        title="Edit Location"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Delete location "${loc.label}"?`)) {
                            deleteLocationMutation.mutate({ id: loc.id });
                          }
                        }}
                        title="Delete Location"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                    <span className="text-card-foreground">{loc.formattedAddress}</span>
                    {loc.accessNotes && (
                      <span className="text-[11px] italic mt-1">Note: {loc.accessNotes}</span>
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-border/40 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`${basePath}/${loc.id}/edit`);
                    }}
                  >
                    <Edit2 className="h-3 w-3" />
                    <span>Edit Location</span>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
